import { Flex, Text } from "@theorgchartdev/storybook";
import { getComparisonLabel } from "@/appComponents/Blocks/Composites/RuleInput/RulePairInput/RulePairInput.util";
import { GetDoc } from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { QRule } from "@coreapp/CoreApp/QRule";
import { QRulePair, enumRulePairLogicCondition } from "@coreapp/CoreApp/QRulePair";
import { ConditionInput } from "@/appComponents/Navigation/RightSidebar/Utils/ConditionInput";

const RulePairContent = ({ rulePair }: { rulePair: QRulePair }) => {
  const doc = GetDoc();
  const fieldName = rulePair.GetFieldName() || "";
  const comparison = rulePair.GetComparison() || "";
  const fieldDef = rulePair.GetReferenceFieldDef?.(doc);
  const valueString = rulePair.GetValueDisplayString?.(doc) || "";
  const comparisonLabel = getComparisonLabel(
    comparison,
    rulePair.IsFieldToFieldComparison?.(),
    fieldDef as any
  );

  return (
    <>
      {"<"}
      {fieldName}
      {">"} <strong>{comparisonLabel}</strong> {"<"}
      {valueString}
      {">"}
    </>
  );
};

// fLogicalOperator uses plain "and"/"or"; ConditionInput expects "$and"/"$or"
const logicalOperatorToCondition = (logicalOperator: string | null): string => {
  return logicalOperator === "and" ? enumRulePairLogicCondition.and : enumRulePairLogicCondition.or;
};

const BasicRuleDisplay = ({
  pairs,
  ruleTypeLabel,
  connectorValue
}: {
  pairs: QRulePair[];
  ruleTypeLabel: string;
  connectorValue: string | null;
}) => (
  <Flex direction="column" gap="4px">
    {pairs.map((pair, index) => (
      <Flex key={pair.fUID ?? index} direction="column" gap="4px">
        {index > 0 && (
          <ConditionInput isEditing={false} value={connectorValue ?? pairs[index - 1].fCondition} />
        )}
        <Text size="small">
          {index === 0 && (
            <strong className="oc-font-weight-500">
              {ruleTypeLabel}
              {": "}
            </strong>
          )}
          <RulePairContent rulePair={pair} />
        </Text>
      </Flex>
    ))}
  </Flex>
);

const ComplexRuleDisplay = ({ rule, ruleTypeLabel }: { rule: QRule; ruleTypeLabel: string }) => (
  <Flex direction="column" gap="4px">
    <Text size="small">
      <strong className="oc-font-weight-500">
        {ruleTypeLabel}
        {": "}
      </strong>
      {rule.GetComplexDef() || ""}
    </Text>
  </Flex>
);

type MinimalFilterRuleDisplayProps = {
  rule: QRule | null;
  parentConnector?: boolean;
};

export const MinimalFilterRuleDisplay = ({
  rule,
  parentConnector = true
}: MinimalFilterRuleDisplayProps) => {
  if (!rule)
    return (
      <Text size="small" className="oc-font-weight-500">
        {appstr("No filter applied")}
      </Text>
    );

  const isComplex = rule.GetIsComplex();
  const ruleTypeLabel = isComplex ? appstr("ComplexRule") : appstr("BasicRule");

  if (isComplex) {
    return <ComplexRuleDisplay rule={rule} ruleTypeLabel={ruleTypeLabel} />;
  }

  const pairs = rule.GetRulePairList();
  if (!pairs || pairs.length === 0) return null;

  const connectorValue = parentConnector
    ? logicalOperatorToCondition(rule.GetLogicalOperator())
    : null;

  return (
    <BasicRuleDisplay pairs={pairs} ruleTypeLabel={ruleTypeLabel} connectorValue={connectorValue} />
  );
};
